import React, { createContext, useEffect, useState, useCallback } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import axios from "axios";
import "@/App.css";
import Welcome from "@/pages/Welcome";
import Landing from "@/pages/Landing";
import CreateProfile from "@/pages/CreateProfile";
import ParentDashboard from "@/pages/ParentDashboard";
import AdminPanel from "@/pages/AdminPanel";
import MainLayout from "@/pages/MainLayout";
import VerifyEmail from "@/pages/VerifyEmail";
import ResetPassword from "@/pages/ResetPassword";
import AccountSettings from "@/pages/AccountSettings";
import FamilyJoin from "@/pages/FamilyJoin";
import Billing from "@/pages/Billing";
import { Privacy, Terms } from "@/pages/LegalPage";
import { Toaster } from "@/components/ui/sonner";

export const UserContext = createContext(null);
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export function getFamilyCode() { return localStorage.getItem("familyCode") || null; }

function persistSession(data) {
  if (data?.token) localStorage.setItem("authToken", data.token);
  if (data?.family?.code) localStorage.setItem("familyCode", data.family.code);
  if (data?.account) localStorage.setItem("account", JSON.stringify(data.account));
}

export function clearSession() {
  localStorage.removeItem("authToken");
  localStorage.removeItem("account");
  localStorage.removeItem("familyCode");
}

axios.interceptors.request.use(config => {
  const token = localStorage.getItem("authToken");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

function AppRoutes() {
  const location = useLocation();
  const { account, currentUser, loading, billing } = React.useContext(UserContext);
  if (loading) return <div className="min-h-screen flex items-center justify-center" style={{ background: "#FFF9F0", color: "#7A5946" }}>Loading Treasure Tome...</div>;

  const isPublicPath = ["/", "/welcome", "/verify-email", "/reset-password", "/billing", "/privacy", "/terms"].includes(location.pathname) || location.pathname.startsWith("/join");
  const isChild = account?.account_type === "family_child";
  const needsBilling = account && billing?.configured && !billing?.access;
  if (needsBilling && !isPublicPath) return <Navigate to="/billing" replace />;

  return <Routes>
    <Route path="/welcome" element={<Welcome />} />
    <Route path="/privacy" element={<Privacy />} />
    <Route path="/terms" element={<Terms />} />
    <Route path="/join" element={<FamilyJoin />} />
    <Route path="/join/:code" element={<FamilyJoin />} />
    <Route path="/verify-email" element={<VerifyEmail />} />
    <Route path="/reset-password" element={<ResetPassword />} />
    <Route path="/billing" element={account ? <Billing /> : <Navigate to="/welcome" replace />} />
    <Route path="/account" element={account && !isChild ? <AccountSettings /> : <Navigate to={isChild ? `/join/${getFamilyCode() || ""}` : "/welcome"} replace />} />
    <Route path="/create-profile" element={account && !currentUser && !isChild ? <CreateProfile /> : <Navigate to={account ? (account.role === "parent" ? "/parent" : "/app") : "/welcome"} replace />} />
    <Route path="/parent" element={account?.role === "parent" ? <ParentDashboard /> : <Navigate to={account ? "/app" : "/welcome"} replace />} />
    <Route path="/admin/requests" element={<Navigate to="/admin" replace />} />
    <Route path="/admin" element={<AdminPanel />} />
    <Route path="/app/*" element={account && currentUser ? <MainLayout /> : <Navigate to={account ? "/create-profile" : "/welcome"} replace />} />
    <Route path="/" element={<Landing />} />
    <Route path="*" element={<Navigate to={account ? "/" : "/welcome"} replace />} />
  </Routes>;
}

function App() {
  const [account, setAccount] = useState(() => { try { return JSON.parse(localStorage.getItem("account")) || null; } catch { return null; } });
  const [currentUser, setCurrentUser] = useState(null);
  const [billing, setBilling] = useState(null);
  const [loading, setLoading] = useState(Boolean(localStorage.getItem("authToken")));

  const refreshBilling = useCallback(async () => {
    if (!localStorage.getItem("authToken")) { setBilling(null); return null; }
    try {
      const { data } = await axios.get(`${API}/billing/status`);
      setBilling(data);
      return data;
    } catch { setBilling(null); return null; }
  }, []);

  const refreshSession = useCallback(async () => {
    const token = localStorage.getItem("authToken");
    if (!token) { setLoading(false); return; }
    try {
      const { data } = await axios.get(`${API}/auth/me`);
      setAccount(data.account);
      setCurrentUser(data.user || null);
      if (data.family?.code) localStorage.setItem("familyCode", data.family.code);
      localStorage.setItem("account", JSON.stringify(data.account));
      await refreshBilling();
    } catch {
      clearSession(); setAccount(null); setCurrentUser(null); setBilling(null);
    } finally { setLoading(false); }
  }, [refreshBilling]);

  useEffect(() => { refreshSession(); }, [refreshSession]);
  const logout = useCallback(() => { clearSession(); setAccount(null); setCurrentUser(null); setBilling(null); window.location.href = "/welcome"; }, []);

  return <UserContext.Provider value={{ currentUser, setCurrentUser, account, setAccount, billing, setBilling, refreshBilling, refreshSession, logout, persistSession }}>
    <BrowserRouter><Toaster position="top-center" richColors /><AppRoutes /></BrowserRouter>
  </UserContext.Provider>;
}

export default App;
